import { API_BASE_URL } from "../../config/apiConfig";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  FaCheckCircle,
  FaClock,
  FaDownload,
  FaEye,
  FaSyncAlt,
  FaTimesCircle,
  FaTrashAlt,
} from "react-icons/fa";

import ProfessionalDocumentSection from "../../components/professional/ProfessionalDocumentSection";

import "../../styles/pages/professional/ProfessionalDocuments.css";

const DOCUMENTS_API_URL =
  `${API_BASE_URL}/professional/documents`;;

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ACCEPTED_FILE_TYPES =
  ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp";

const MIME_TYPES_BY_EXTENSION = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  doc: "application/msword",
  docx:
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

const DOCUMENT_GROUPS = {
  identity: [
    { id: 6, name: "Passport" },
    { id: 2, name: "Driver's Licence" },
    { id: 10, name: "Permanent Resident Card" },
    { id: 11, name: "Work Permit" },
  ],

  required: [
    { id: 4, name: "Resume" },
    { id: 7, name: "Immunization Record" },
    { id: 15, name: "Vulnerable Sector Check" },
  ],

  compliance: [
    { id: 1, name: "Police Check" },
    { id: 3, name: "TB Test" },
    { id: 9, name: "Mask Fit" },
    { id: 13, name: "N95 Fit Test" },
  ],

  business: [
    {
      id: 12,
      name: "Certificate of Incorporation",
    },
  ],

  education: [
    { id: 5, name: "Nursing Degree" },
    { id: 14, name: "Diploma" },
  ],

  additional: [
    {
      id: 8,
      name: "Additional Supporting Document",
    },
  ],
};

const SECTION_DETAILS = {
  identity: {
    title: "Identity Verification",
    description:
      "Upload at least one government-issued identity document. You may upload additional identity documents.",
    placeholder: "Select identity document",
  },

  required: {
    title: "Required Professional Documents",
    description:
      "Upload your resume, immunization record, and Vulnerable Sector Check.",
    placeholder: "Select required document",
  },

  compliance: {
    title: "Additional Compliance Documents",
    description:
      "Upload any additional compliance documents available to you.",
    placeholder: "Select compliance document",
  },

  business: {
    title: "Business Document",
    description:
      "Upload your Certificate of Incorporation if you operate through a registered business.",
    placeholder: "Select business document",
  },

  education: {
    title: "Education",
    description:
      "Upload your nursing degree, diploma, or both.",
    placeholder: "Select education document",
  },

  additional: {
    title: "Additional Supporting Documents",
    description:
      "Upload other documents that support your professional profile.",
    placeholder: "Select supporting document",
  },
};

const EMPTY_SECTION_VALUES = {
  identity: "",
  required: "",
  compliance: "",
  business: "",
  education: "",
  additional: "",
};

const EMPTY_SECTION_FILES = {
  identity: null,
  required: null,
  compliance: null,
  business: null,
  education: null,
  additional: null,
};

const getToken = () =>
  localStorage.getItem("zethAuthToken") ||
  sessionStorage.getItem("zethAuthToken");

const getSectionKeyFromTypeId = (
  documentTypeId
) => {
  const numericId = Number(documentTypeId);

  const matchingSection = Object.entries(
    DOCUMENT_GROUPS
  ).find(([, documentTypes]) =>
    documentTypes.some(
      (documentType) =>
        documentType.id === numericId
    )
  );

  return matchingSection?.[0] || "additional";
};

const normalizeDocument = (document) => {
  const documentTypeId = Number(
    document.document_type_id ??
      document.documentTypeId
  );

  return {
    id: document.id,

    documentTypeId,

    sectionKey:
      document.sectionKey ||
      getSectionKeyFromTypeId(
        documentTypeId
      ),

    name:
      document.document_type_name ||
      document.name ||
      "Professional Document",

    fileName:
      document.original_name ||
      document.file_name ||
      document.fileName ||
      "Uploaded document",

    fileSize:
      Number(
        document.file_size ??
          document.fileSize
      ) || 0,

    mimeType:
      document.mime_type ||
      document.mimeType ||
      "",

    verificationStatus:
      document.verification_status ||
      document.verificationStatus ||
      "pending",

    uploadedAt:
      document.uploaded_at ||
      document.created_at ||
      document.uploadedAt ||
      new Date().toISOString(),

    expiryDate:
      document.expiry_date ||
      document.expiryDate ||
      null,
  };
};

const parseJsonResponse = async (response) => {
  const contentType =
    response.headers.get("content-type");

  if (
    !contentType?.includes(
      "application/json"
    )
  ) {
    throw new Error(
      "The server returned an invalid response."
    );
  }

  return response.json();
};

const getFileExtension = (fileName = "") =>
  fileName
    .split(".")
    .pop()
    ?.toLowerCase();

function ProfessionalDocuments() {
  const fileInputRefs = useRef({});

  const [
    selectedTypes,
    setSelectedTypes,
  ] = useState(EMPTY_SECTION_VALUES);

  const [
    selectedFiles,
    setSelectedFiles,
  ] = useState(EMPTY_SECTION_FILES);

  const [
    uploadedDocuments,
    setUploadedDocuments,
  ] = useState([]);

  const [
    loadingDocuments,
    setLoadingDocuments,
  ] = useState(true);

  const [
    uploadingSection,
    setUploadingSection,
  ] = useState("");

  const [
    replacingDocumentId,
    setReplacingDocumentId,
  ] = useState(null);

  const [
    deletingDocumentId,
    setDeletingDocumentId,
  ] = useState(null);

  const [
    documentError,
    setDocumentError,
  ] = useState("");

  const [
    documentMessage,
    setDocumentMessage,
  ] = useState("");

  const clearMessages = () => {
    setDocumentError("");
    setDocumentMessage("");
  };

  const loadDocuments =
    useCallback(async () => {
      try {
        setLoadingDocuments(true);
        setDocumentError("");

        const token = getToken();

        if (!token) {
          throw new Error(
            "Authentication required. Please sign in again."
          );
        }

        const response = await fetch(
          DOCUMENTS_API_URL,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

        const data =
          await parseJsonResponse(response);

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.message ||
              "Unable to load documents."
          );
        }

        const documents =
          Array.isArray(data.documents)
            ? data.documents.map(
                normalizeDocument
              )
            : [];

        setUploadedDocuments(documents);
      } catch (error) {
        console.error(
          "Load documents error:",
          error
        );

        setDocumentError(
          error.message ||
            "Unable to load documents."
        );
      } finally {
        setLoadingDocuments(false);
      }
    }, []);

  useEffect(() => {
    document.title =
      "Professional Documents | Zeth Health";
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const fetchDocumentBlob =
    async (uploadedDocument) => {
      const token = getToken();

      if (!token) {
        throw new Error(
          "Authentication required. Please sign in again."
        );
      }

      const response = await fetch(
        `${DOCUMENTS_API_URL}/${uploadedDocument.id}/view`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response
          .json()
          .catch(() => null);

        throw new Error(
          data?.message ||
            "Unable to retrieve document."
        );
      }

      return response.blob();
    };

  const handleViewDocument =
    async (uploadedDocument) => {
      try {
        setDocumentError("");

        const responseBlob =
          await fetchDocumentBlob(
            uploadedDocument
          );

        const extension =
          getFileExtension(
            uploadedDocument.fileName
          );

        const correctedBlob = new Blob(
          [responseBlob],
          {
            type:
              MIME_TYPES_BY_EXTENSION[
                extension
              ] ||
              responseBlob.type ||
              "application/octet-stream",
          }
        );

        const blobUrl =
          URL.createObjectURL(
            correctedBlob
          );

        window.open(
          blobUrl,
          "_blank",
          "noopener,noreferrer"
        );

        window.setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000);
      } catch (error) {
        console.error(
          "View document error:",
          error
        );

        setDocumentError(
          error.message ||
            "Unable to open document."
        );
      }
    };

  const handleDownloadDocument =
    async (uploadedDocument) => {
      try {
        setDocumentError("");

        const blob =
          await fetchDocumentBlob(
            uploadedDocument
          );

        const blobUrl =
          URL.createObjectURL(blob);

        const downloadLink =
          window.document.createElement(
            "a"
          );

        downloadLink.href = blobUrl;

        downloadLink.download =
          uploadedDocument.fileName ||
          "document";

        window.document.body.appendChild(
          downloadLink
        );

        downloadLink.click();
        downloadLink.remove();

        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error(
          "Download document error:",
          error
        );

        setDocumentError(
          error.message ||
            "Unable to download document."
        );
      }
    };

  const handleReplaceDocument =
    (uploadedDocument) => {
      const replacementInput =
        window.document.createElement(
          "input"
        );

      replacementInput.type = "file";
      replacementInput.accept =
        ACCEPTED_FILE_TYPES;

      replacementInput.onchange =
        async (event) => {
          const replacementFile =
            event.target.files?.[0];

          if (!replacementFile) {
            return;
          }

          if (
            replacementFile.size >
            MAX_FILE_SIZE
          ) {
            window.alert(
              "The selected file is larger than 10 MB."
            );
            return;
          }

          const confirmed =
            window.confirm(
              `Replace ${uploadedDocument.name} with "${replacementFile.name}"?\n\nIts verification status will return to Pending Verification.`
            );

          if (!confirmed) {
            return;
          }

          try {
            setReplacingDocumentId(
              uploadedDocument.id
            );

            clearMessages();

            const token = getToken();

            if (!token) {
              throw new Error(
                "Authentication required. Please sign in again."
              );
            }

            const formData =
              new FormData();

            formData.append(
              "file",
              replacementFile
            );

            const response = await fetch(
              `${DOCUMENTS_API_URL}/${uploadedDocument.id}`,
              {
                method: "PUT",

                headers: {
                  Authorization:
                    `Bearer ${token}`,
                },

                body: formData,
              }
            );

            const data =
              await parseJsonResponse(
                response
              );

            if (
              !response.ok ||
              !data.success
            ) {
              throw new Error(
                data.message ||
                  "Unable to replace document."
              );
            }

            const replacedDocument =
              normalizeDocument(
                data.document
              );

            setUploadedDocuments(
              (currentDocuments) =>
                currentDocuments.map(
                  (currentDocument) =>
                    currentDocument.id ===
                    uploadedDocument.id
                      ? replacedDocument
                      : currentDocument
                )
            );

            setDocumentMessage(
              data.message ||
                "Document replaced successfully."
            );
          } catch (error) {
            console.error(
              "Replace document error:",
              error
            );

            setDocumentError(
              error.message ||
                "Unable to replace document."
            );
          } finally {
            setReplacingDocumentId(
              null
            );
          }
        };

      replacementInput.click();
    };

  const handleDeleteDocument =
    async (uploadedDocument) => {
      const confirmed =
        window.confirm(
          `Delete ${uploadedDocument.name}?\n\nThe document will be removed from your profile.`
        );

      if (!confirmed) {
        return;
      }

      try {
        setDeletingDocumentId(
          uploadedDocument.id
        );

        clearMessages();

        const token = getToken();

        if (!token) {
          throw new Error(
            "Authentication required. Please sign in again."
          );
        }

        const response = await fetch(
          `${DOCUMENTS_API_URL}/${uploadedDocument.id}`,
          {
            method: "DELETE",

            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

        const data =
          await parseJsonResponse(response);

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.message ||
              "Unable to delete document."
          );
        }

        setUploadedDocuments(
          (currentDocuments) =>
            currentDocuments.filter(
              (currentDocument) =>
                currentDocument.id !==
                uploadedDocument.id
            )
        );

        setDocumentMessage(
          data.message ||
            "Document deleted successfully."
        );
      } catch (error) {
        console.error(
          "Delete document error:",
          error
        );

        setDocumentError(
          error.message ||
            "Unable to delete document."
        );
      } finally {
        setDeletingDocumentId(null);
      }
    };

  const handleSelectionChange = (
    sectionKey,
    value
  ) => {
    setSelectedTypes(
      (currentSelections) => ({
        ...currentSelections,
        [sectionKey]: value,
      })
    );

    clearMessages();
  };

  const handleFileChange = (
    sectionKey,
    event
  ) => {
    const selectedFile =
      event.target.files?.[0] || null;

    clearMessages();

    if (!selectedFile) {
      setSelectedFiles(
        (currentFiles) => ({
          ...currentFiles,
          [sectionKey]: null,
        })
      );

      return;
    }

    if (
      selectedFile.size >
      MAX_FILE_SIZE
    ) {
      window.alert(
        "The selected file is larger than 10 MB."
      );

      event.target.value = "";

      setSelectedFiles(
        (currentFiles) => ({
          ...currentFiles,
          [sectionKey]: null,
        })
      );

      return;
    }

    setSelectedFiles(
      (currentFiles) => ({
        ...currentFiles,
        [sectionKey]: selectedFile,
      })
    );
  };

  const clearSectionInputs = (
    sectionKey
  ) => {
    setSelectedTypes(
      (currentSelections) => ({
        ...currentSelections,
        [sectionKey]: "",
      })
    );

    setSelectedFiles(
      (currentFiles) => ({
        ...currentFiles,
        [sectionKey]: null,
      })
    );

    const fileInput =
      fileInputRefs.current[sectionKey];

    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleDocumentUpload =
    async (sectionKey) => {
      const selectedTypeValue =
        selectedTypes[sectionKey];

      const selectedFile =
        selectedFiles[sectionKey];

      if (!selectedTypeValue) {
        window.alert(
          "Please select a document type before uploading."
        );
        return;
      }

      if (!selectedFile) {
        window.alert(
          "Please choose a file before uploading."
        );
        return;
      }

      try {
        setUploadingSection(sectionKey);
        clearMessages();

        const token = getToken();

        if (!token) {
          throw new Error(
            "Authentication required. Please sign in again."
          );
        }

        const formData = new FormData();

        formData.append(
          "file",
          selectedFile
        );

        formData.append(
          "documentTypeId",
          selectedTypeValue
        );

        const response = await fetch(
          DOCUMENTS_API_URL,
          {
            method: "POST",

            headers: {
              Authorization:
                `Bearer ${token}`,
            },

            body: formData,
          }
        );

        const data =
          await parseJsonResponse(response);

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.message ||
              "Unable to upload document."
          );
        }

        const savedDocument =
          normalizeDocument(
            data.document
          );

        setUploadedDocuments(
          (currentDocuments) => [
            ...currentDocuments.filter(
              (document) =>
                document.id !==
                savedDocument.id
            ),
            savedDocument,
          ]
        );

        setDocumentMessage(
          data.message ||
            "Document uploaded successfully."
        );

        clearSectionInputs(sectionKey);
      } catch (error) {
        console.error(
          "Upload document error:",
          error
        );

        setDocumentError(
          error.message ||
            "Unable to upload document."
        );
      } finally {
        setUploadingSection("");
      }
    };

  const getDocumentsForSection = (
    sectionKey
  ) =>
    uploadedDocuments.filter(
      (document) =>
        document.sectionKey ===
        sectionKey
    );

  const uploadedIdentityDocuments =
    getDocumentsForSection("identity");

  const identityComplete =
    uploadedIdentityDocuments.length > 0;

  const hasUploadedDocument = (
    documentTypeId
  ) =>
    uploadedDocuments.some(
      (document) =>
        document.documentTypeId ===
        documentTypeId
    );

  const requiredDocumentsComplete =
    DOCUMENT_GROUPS.required.every(
      (document) =>
        hasUploadedDocument(document.id)
    );

  const requiredItemCount = 4;

  const completedRequiredItems = [
    identityComplete,
    hasUploadedDocument(4),
    hasUploadedDocument(7),
    hasUploadedDocument(15),
  ].filter(Boolean).length;

  const completionPercentage =
    Math.round(
      (completedRequiredItems /
        requiredItemCount) *
        100
    );

  const optionalUploadedCount = [
    "business",
    "compliance",
    "education",
    "additional",
  ].reduce(
    (count, sectionKey) =>
      count +
      getDocumentsForSection(
        sectionKey
      ).length,
    0
  );

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "Date unavailable";
    }

    const date = new Date(dateValue);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "Date unavailable";
    }

    return new Intl.DateTimeFormat(
      "en-CA",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    ).format(date);
  };

  const formatFileSize = (fileSize) => {
    if (
      typeof fileSize !== "number" ||
      fileSize <= 0
    ) {
      return "";
    }

    if (fileSize < 1024) {
      return `${fileSize} B`;
    }

    if (
      fileSize <
      1024 * 1024
    ) {
      return `${(
        fileSize / 1024
      ).toFixed(1)} KB`;
    }

    return `${(
      fileSize /
      (1024 * 1024)
    ).toFixed(1)} MB`;
  };

  const getNormalizedStatus = (
    status
  ) =>
    String(status || "pending")
      .trim()
      .toLowerCase();

  const formatVerificationStatus = (
    status
  ) => {
    const normalizedStatus =
      getNormalizedStatus(status);

    if (
      normalizedStatus === "verified"
    ) {
      return "Verified";
    }

    if (
      normalizedStatus === "rejected"
    ) {
      return "Rejected";
    }

    if (
      normalizedStatus === "expired"
    ) {
      return "Expired";
    }

    return "Pending Verification";
  };

  const getVerificationClass = (
    status
  ) => {
    const normalizedStatus =
      getNormalizedStatus(status);

    if (
      normalizedStatus === "verified"
    ) {
      return "verified";
    }

    if (
      normalizedStatus === "rejected"
    ) {
      return "rejected";
    }

    if (
      normalizedStatus === "expired"
    ) {
      return "expired";
    }

    return "pending";
  };

  const getStatusIcon = (status) => {
    const normalizedStatus =
      getNormalizedStatus(status);

    if (
      normalizedStatus === "verified"
    ) {
      return (
        <FaCheckCircle
          aria-hidden="true"
        />
      );
    }

    if (
      normalizedStatus === "rejected" ||
      normalizedStatus === "expired"
    ) {
      return (
        <FaTimesCircle
          aria-hidden="true"
        />
      );
    }

    return (
      <FaClock aria-hidden="true" />
    );
  };

  const getSectionBadge = (
    sectionKey
  ) => {
    if (
      sectionKey === "identity"
    ) {
      return identityComplete
        ? "Completed"
        : "Required";
    }

    if (
      sectionKey === "required"
    ) {
      return requiredDocumentsComplete
        ? "Completed"
        : "Required";
    }

    return "Optional";
  };

  const renderDocumentActions = (
    uploadedDocument
  ) => {
    const isReplacing =
      replacingDocumentId ===
      uploadedDocument.id;

    const isDeleting =
      deletingDocumentId ===
      uploadedDocument.id;

    const isBusy =
      isReplacing || isDeleting;

    return (
      <div className="document-actions">
        <button
          type="button"
          className="document-action-button document-view-button"
          onClick={() =>
            handleViewDocument(
              uploadedDocument
            )
          }
          disabled={isBusy}
        >
          <FaEye aria-hidden="true" />
          <span>View</span>
        </button>

        <button
          type="button"
          className="document-action-button document-download-button"
          onClick={() =>
            handleDownloadDocument(
              uploadedDocument
            )
          }
          disabled={isBusy}
        >
          <FaDownload
            aria-hidden="true"
          />
          <span>Download</span>
        </button>

        <button
          type="button"
          className="document-action-button document-replace-button"
          onClick={() =>
            handleReplaceDocument(
              uploadedDocument
            )
          }
          disabled={isBusy}
        >
          <FaSyncAlt
            aria-hidden="true"
          />

          <span>
            {isReplacing
              ? "Replacing..."
              : "Replace"}
          </span>
        </button>

        <button
          type="button"
          className="document-action-button document-delete-button"
          onClick={() =>
            handleDeleteDocument(
              uploadedDocument
            )
          }
          disabled={isBusy}
        >
          <FaTrashAlt
            aria-hidden="true"
          />

          <span>
            {isDeleting
              ? "Deleting..."
              : "Delete"}
          </span>
        </button>
      </div>
    );
  };

  const renderUploadedDocuments = (
    sectionKey
  ) => {
    const documents =
      getDocumentsForSection(
        sectionKey
      );

    if (documents.length === 0) {
      return (
        <p className="documents-empty-text">
          No document uploaded yet.
        </p>
      );
    }

    return (
      <div className="uploaded-document-list">
        {documents.map(
          (uploadedDocument) => (
            <article
              key={uploadedDocument.id}
              className="uploaded-document-item"
            >
              <div className="uploaded-document-details">
                <strong>
                  {uploadedDocument.name}
                </strong>

                <span>
                  {
                    uploadedDocument.fileName
                  }
                </span>

                <small>
                  Uploaded{" "}
                  {formatDate(
                    uploadedDocument.uploadedAt
                  )}

                  {uploadedDocument.fileSize
                    ? ` • ${formatFileSize(
                        uploadedDocument.fileSize
                      )}`
                    : ""}
                </small>
              </div>

              <div className="uploaded-document-status">
                <span
                  className={`document-status-badge ${getVerificationClass(
                    uploadedDocument.verificationStatus
                  )}`}
                >
                  {getStatusIcon(
                    uploadedDocument.verificationStatus
                  )}

                  <span>
                    {formatVerificationStatus(
                      uploadedDocument.verificationStatus
                    )}
                  </span>
                </span>

                {renderDocumentActions(
                  uploadedDocument
                )}
              </div>
            </article>
          )
        )}
      </div>
    );
  };

  const renderDocumentSection = (
    sectionKey
  ) => {
    const details =
      SECTION_DETAILS[sectionKey];

    const documentOptions =
      DOCUMENT_GROUPS[sectionKey];

    const isUploading =
      uploadingSection === sectionKey;

    const uploadEnabled =
      Boolean(
        selectedTypes[sectionKey]
      ) &&
      Boolean(
        selectedFiles[sectionKey]
      ) &&
      !isUploading;

    return (
      <ProfessionalDocumentSection
        key={sectionKey}
        title={details.title}
        description={
          details.description
        }
        badge={getSectionBadge(
          sectionKey
        )}
      >
        <article className="document-upload-panel">
          <div className="document-dropdown-upload">
            <div className="document-dropdown-field">
              <label
                htmlFor={`${sectionKey}-document-type`}
              >
                Select document
              </label>

              <select
                id={`${sectionKey}-document-type`}
                value={
                  selectedTypes[
                    sectionKey
                  ]
                }
                onChange={(event) =>
                  handleSelectionChange(
                    sectionKey,
                    event.target.value
                  )
                }
                disabled={isUploading}
              >
                <option value="">
                  {details.placeholder}
                </option>

                {documentOptions.map(
                  (documentType) => (
                    <option
                      key={documentType.id}
                      value={
                        documentType.id
                      }
                    >
                      {
                        documentType.name
                      }
                    </option>
                  )
                )}
              </select>
            </div>

            <div className="document-file-field">
              <label
                htmlFor={`${sectionKey}-document-file`}
              >
                Choose file
              </label>

              <input
                ref={(element) => {
                  fileInputRefs.current[
                    sectionKey
                  ] = element;
                }}
                id={`${sectionKey}-document-file`}
                type="file"
                accept={
                  ACCEPTED_FILE_TYPES
                }
                onChange={(event) =>
                  handleFileChange(
                    sectionKey,
                    event
                  )
                }
                disabled={isUploading}
              />
            </div>

            <button
              type="button"
              className="document-upload-button"
              onClick={() =>
                handleDocumentUpload(
                  sectionKey
                )
              }
              disabled={!uploadEnabled}
            >
              {isUploading
                ? "Uploading..."
                : "Upload Document"}
            </button>
          </div>

          <p className="document-upload-help">
            PDF, DOC, DOCX, JPG, JPEG,
            PNG or WEBP. Maximum file
            size: 10 MB.
          </p>

          <div className="document-uploaded-area">
            <h4>
              Uploaded Documents
            </h4>

            {renderUploadedDocuments(
              sectionKey
            )}
          </div>
        </article>
      </ProfessionalDocumentSection>
    );
  };

  return (
    <main className="professional-documents">
      <div className="credential-child-description">
        <p>
          Upload, manage and track the
          verification status of your
          professional documents.
        </p>
      </div>

      {loadingDocuments && (
        <div className="document-page-message">
          Loading your documents...
        </div>
      )}

      {documentError && (
        <div
          className="document-page-message document-page-message-error"
          role="alert"
        >
          {documentError}
        </div>
      )}

      {documentMessage && (
        <div
          className="document-page-message document-page-message-success"
          role="status"
        >
          {documentMessage}
        </div>
      )}

      <section className="documents-progress-card">
        <div className="documents-progress-header">
          <div>
            <h2>
  Required Document Completion
</h2>

            <p>
              {completedRequiredItems} of{" "}
{requiredItemCount} required
documents uploaded.
            </p>
          </div>

          <strong>
            {completionPercentage}%
          </strong>
        </div>

        <div className="documents-progress-bar">
          <div
            className="documents-progress-fill"
            style={{
              width:
                `${completionPercentage}%`,
            }}
          />
        </div>
      </section>

      <section className="documents-content">
        <section className="documents-group-card documents-group-card-required">
          <div className="documents-group-header">
            <div>
              <p className="account-label">
                Required
              </p>

              <h2 className="documents-group-title">
                Required Documents
              </h2>

              <p>
                Complete these required
                documents to verify your
                profile and become
                assignment-ready.
              </p>
            </div>

            <span className="documents-group-status">
              {completedRequiredItems} of{" "}
              {requiredItemCount} completed
            </span>
          </div>

          <div className="documents-group-sections">
            {renderDocumentSection(
              "identity"
            )}

            {renderDocumentSection(
              "required"
            )}
          </div>
        </section>

        <section className="documents-group-card documents-group-card-optional">
          <div className="documents-group-header">
            <div>
              <h2 className="documents-group-title optional">
                Optional Documents
              </h2>

              <p>
                Add documents that
                strengthen your
                professional profile and
                may improve assignment
                opportunities.
              </p>
            </div>

            <span className="documents-group-status">
              {optionalUploadedCount}{" "}
              uploaded
            </span>
          </div>

          <div className="documents-group-sections">
            {renderDocumentSection(
              "business"
            )}

            {renderDocumentSection(
              "compliance"
            )}

            {renderDocumentSection(
              "education"
            )}

            {renderDocumentSection(
              "additional"
            )}
          </div>
        </section>
      </section>
    </main>
  );
}

export default ProfessionalDocuments;
